import React, { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'

import { WidgetLayout } from '../../../lib/widget-layout'
import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import { theme } from '../../../lib/theme'
import useText from '../../../common/hooks/useText'
import Text from '../../../ui/Text'
import { TooltipIcon } from '../../../lib/icons/tooltip-icon'
import { TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import {
    ChargePoint,
    LoadBalancerGroup,
    useLoadBalancerGroupValidateChargePointImportsMutation,
    useLoadBalancerGroupImportChargePointsMutation,
    ChargePointImport,
} from '../../../common/redux/api/web-api'
import Input from '../../../common/components/Input'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { SAlertDiv, STextArea } from '../../../admin/components/ImportChargePointModal'
import { useValidateImportChargePointsinLBGroupMutation } from '../../../admin/redux/api/chargePointsApi'
import { ErrorMsg } from '../../../ui/loadBalancer/LoadBalancer.styles'
import {
    SOcppSettingsHeading,
    SOcppSettingsHeadingIcon,
} from '../../../common/features/ocpp-settings/OcppSettings.style'
import InlineAlert, { AlertType } from '../../../common/components/InlineAlert'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'

import LBChargePointSettings from './LoadBalancerChargePointSettings'

type ImportChargePointToLBType = {
    showModal: (show: boolean) => void
    group: LoadBalancerGroup
    chargePointsInGroup: Array<ChargePoint>
    updateChargePoints: () => void
}
const STableContainer = styled.div`
    width: 100%;
    max-height: 300px;
    overflow: auto;
    &::-webkit-scrollbar {
        display: none;
    }

    & > table {
        width: auto;
        min-width: 100%;
        margin-bottom: unset;
    }
`

const lbTypes = { Unknown: 'Unknown', Invalid: 'Invalid', None: 'None', Internal: 'Internal' }

export default ({
    showModal,
    group,
    chargePointsInGroup,
    updateChargePoints,
}: ImportChargePointToLBType): ReactElement => {
    const text = useText()
    const [chargePoints, setChargePoints] = useState<Array<ChargePointImport>>([])
    const [selectedChargePoints, setSelectedChargePoints] = useState<Array<ChargePointImport>>(() => [])
    const [lbType, setLbType] = useState<string | undefined>(lbTypes.Unknown)
    const [validationError, setValidationError] = useState<string>('')
    const [allSelected, setAllSelected] = useState(false)
    const [showLBSettings, setShowLBSettings] = useState(false)
    const [pastedLines, setPastedLines] = useState<string>('')
    const [showInfo, setShowInfo] = useState(false)
    const [visible, setVisible] = useState(false)

    const adaptConnectorNaming = useAdaptConnectorNaming()
    const adaptErrorModalLBAddCharger = (err?: string): string => {
        let errorMsg = ''
        const name = err?.slice(0, err.indexOf(':'))

        switch (err) {
            case 'Charger: msgLbOfflineCurrentNotset':
            case 'Socket: msgLbOfflineCurrentNotset':
            case 'Left socket: msgLbOfflineCurrentNotset':
            case 'Right socket: msgLbOfflineCurrentNotset':
                errorMsg = text('msgErrorAddChargersLBOffineCurrentNotSet', {
                    connectorType: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgLbOfflinePhaseNotset':
            case 'Socket: msgLbOfflinePhaseNotset':
            case 'Left socket: msgLbOfflinePhaseNotset':
            case 'Right socket: msgLbOfflinePhaseNotset':
                errorMsg = text('msgErrorAddChargersLBOffinePhaseNotSet', {
                    connectorType: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgInstallationCurrentHigher':
            case 'Socket: msgInstallationCurrentHigher':
            case 'Left connector: msgInstallationCurrentHigher':
            case 'Right connector: msgInstallationCurrentHigher':
            case 'Left socket: msgInstallationCurrentHigher':
            case 'Right socket: msgInstallationCurrentHigher':
                errorMsg = text('msgInstallationCurrentHigherThanMaxCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgInstallationCurrentLower':
            case 'Socket: msgInstallationCurrentLower':
            case 'Left socket: msgInstallationCurrentLower':
            case 'Right socket: msgInstallationCurrentLower':
                errorMsg = text('msgInstallationCurrentLowerThanMinCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgErrorLbOfflineCurrentHigher':
            case 'Socket: msgErrorLbOfflineCurrentHigher':
            case 'Left socket: msgErrorLbOfflineCurrentHigher':
            case 'Right socket: msgErrorLbOfflineCurrentHigher':
                errorMsg = text('msgErrorLBOfflineCurrentHigher', {
                    connectorType: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgUserCurrentHigher':
            case 'Socket: msgUserCurrentHigher':
            case 'Left socket: msgUserCurrentHigher':
            case 'Right socket: msgUserCurrentHigher':
                errorMsg = text('msgUserCurrentHigherThanInstallationCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgUserCurrentLower':
            case 'Socket: msgUserCurrentLower':
            case 'Left socket: msgUserCurrentLower':
            case 'Right socket: msgUserCurrentLower':
                errorMsg = text('msgUserCurrentLowerThanMinCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Connected to ocpp server which is not supported at the moment':
                errorMsg = text('msgErrorAddChargersLBConnectedNotSupported')
                break
            case 'msgLbGroupChargerEarthingSystemMismatch':
                errorMsg = text('msgLbGroupChargerEarthingSystemMismatch')
                break
            case 'Socket: msgPhaseInvalidForEarthingSystem':
                errorMsg = text('msgPhaseInvalidForEarthingSystem')
                break
            default:
                err && err?.length > 0 ? (errorMsg = err) : (errorMsg = '')
        }

        return errorMsg
    }

    /** Get organisation's chargepoints **/

    const [validateImportChargePointInCompany, { isLoading }] = useLoadBalancerGroupValidateChargePointImportsMutation(
        {}
    )
    const [validateChargePointbyPaste] = useValidateImportChargePointsinLBGroupMutation()
    const [importChargePoints] = useLoadBalancerGroupImportChargePointsMutation({})

    useEffect(() => {
        const validateImportChargePointsList = async () => {
            try {
                const response = await validateImportChargePointInCompany({
                    id: group?.id,
                    companyId: group?.companyId,
                }).unwrap()
                setChargePoints(response)
            } catch (error) {
                notifyError(text('lblLoadbalancerImportFail'))
            }
        }
        validateImportChargePointsList()
    }, [group?.companyId, group?.id, validateImportChargePointInCompany])

    const doImportChargePoints = async () => {
        importChargePoints({
            id: group?.id,
            body: selectedChargePoints,
        })
            .unwrap()
            .then(() => {
                notifySuccess(text('lblLoadbalancerImportSuccess'))
            })
            .catch(() => notifyError(text('lblLoadbalancerImportFail')))
            .finally(() => {
                updateChargePoints()
                showModal(false)
            })
    }

    /***********************************/

    /** Get chargepoint by paste **/

    const doValidateImport = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardText = event.clipboardData.getData('text')

        setPastedLines(clipboardText.trim())
        try {
            const response = await validateChargePointbyPaste({
                id: group?.id,
                importData: clipboardText,
            }).unwrap()
            setChargePoints(response)
            setValidationError('')
        } catch (error) {
            setValidationError(text('msgImportChargersFailed'))
        }
    }

    /******************************/

    /** Check errors in selected charge points */
    const checkErrors = () => {
        if (selectedChargePoints.length) {
            return selectedChargePoints.some((point) => point.hasError)
        } else {
            return false
        }
    }

    /*****************************************/

    /** Check if charger has errors without */
    const checkOtherErrors = (chargePoint: ChargePointImport) => {
        const validErrors = ['msgLbOfflineCurrentNotset', 'msgLbOfflinePhaseNotset', 'msgErrorLbOfflineCurrentHigher']

        return (
            chargePoint.hasError &&
            chargePoint.errorReason &&
            chargePoint.errorReason.filter((error) => !validErrors.some((element) => error.includes(element))).length >
                0
        )
    }
    /*****/

    useEffect(() => {
        if (!allSelected && chargePointsInGroup.length === 0 && selectedChargePoints.length === 0) {
            setLbType(lbTypes.Unknown)
        } else if (chargePointsInGroup?.length > 0) {
            setLbType(chargePointsInGroup[0]?.ocppServerMode)
        }
    }, [chargePointsInGroup.length, selectedChargePoints.length, allSelected, chargePointsInGroup])

    /*******************/

    /** Select chargepoints */
    const updateSelectedChargePoints = (chargePoint: ChargePointImport, select: boolean) => {
        if (select) {
            if (!selectedChargePoints.length && chargePoint?.ocppServerMode) {
                setLbType(chargePoint?.ocppServerMode)
            }
            setSelectedChargePoints((preValue) => [...preValue, chargePoint])
        } else {
            setSelectedChargePoints((preValue) => [...preValue.filter((item) => item.id !== chargePoint.id)])
            setAllSelected(false)
        }
    }

    const toggleSelectAll = (select: boolean) => {
        if (select) {
            const selected = chargePoints?.filter((c) => {
                if (checkOtherErrors(c)) return false
                if (lbType !== lbTypes.Unknown && lbType !== lbTypes.Invalid) {
                    return c.ocppServerMode === lbType
                } else if (selectedChargePoints.length) {
                    return selectedChargePoints[0].ocppServerMode === c.ocppServerMode
                } else {
                    return c.ocppServerMode === chargePoints[0].ocppServerMode
                }
            })

            if (!selectedChargePoints.length && selected[0]?.ocppServerMode) setLbType(selected[0].ocppServerMode)

            setSelectedChargePoints(selected)
            setAllSelected(true)
        } else {
            setSelectedChargePoints([])
            setAllSelected(false)
        }
    }
    /**********************/

    const startImport = () => {
        if (!checkErrors()) {
            doImportChargePoints()
        } else {
            setShowLBSettings(true)
        }
    }
    /***/

    return (
        <>
            {!showLBSettings && (
                <BootstrapModalLayout
                    size={'lg'}
                    titleButton={
                        <div onClick={() => setShowInfo(!showInfo)} data-testid="info-icon">
                            <TooltipIcon size={22}></TooltipIcon>
                        </div>
                    }
                    loading={isLoading}
                    actions={[
                        {
                            label: text('btnCancel'),
                            callback: () => showModal(false),
                            btn: 'btn-secondary',
                        },
                        {
                            label: text('btnImport'),
                            callback: () => startImport(),
                            disabled: !selectedChargePoints.length,
                        },
                    ]}
                    title={
                        <>
                            <Text code="lblChargersImportModal" />
                            {isLoading && <span className="fa fa-spinner fa-pulse ml-3" />}
                        </>
                    }
                >
                    {showInfo && (
                        <SAlertDiv className="alert alert-info" data-testid="info-msg">
                            {<Text code="msgImportChargersFileTipVersionTwo" />}
                        </SAlertDiv>
                    )}

                    <InlineAlert
                        text={text('msgYouCantMixChargePointsInLBGroup')}
                        alertType={AlertType.Warning}
                        data-testid="wanrning-msg"
                    />
                    <SOcppSettingsHeading
                        className="heading5"
                        style={{ paddingBottom: '15px' }}
                        onClick={() => {
                            setVisible(!visible)
                            setPastedLines('')
                            setValidationError('')
                        }}
                        data-testid="paste-button"
                    >
                        <Text code="lblChargersImportHeading"></Text>
                        <SOcppSettingsHeadingIcon>
                            <i className={`fa ${visible ? 'fa-caret-up' : 'fa-caret-down'}`} />
                        </SOcppSettingsHeadingIcon>
                    </SOcppSettingsHeading>

                    {validationError && <ErrorMsg>{validationError}</ErrorMsg>}
                    {visible && (
                        <STextArea
                            className="form-control"
                            placeholder={text('msgPasteLinesField')}
                            defaultValue={pastedLines}
                            onPaste={doValidateImport}
                            data-testid="paste-textarea"
                        />
                    )}

                    {((chargePoints && chargePoints?.length > 0 && !visible && !pastedLines) ||
                        (visible && pastedLines && !validationError)) && (
                        <WidgetLayout>
                            <STableContainer>
                                <table className="table table-striped table-hover table-responsive ">
                                    <TableHeader className="font-normal">
                                        <TableRow>
                                            <TableHeaderCell className="font-normal">
                                                <Input
                                                    id="error-checkbox"
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={(event) => toggleSelectAll(event.target.checked)}
                                                    data-testid="checkbox-header"
                                                    disabled={chargePoints.length === 0}
                                                />
                                            </TableHeaderCell>
                                            <TableHeaderCell>{text('lblChargerType')}</TableHeaderCell>
                                            <TableHeaderCell>{text('lblSerialNumber')}</TableHeaderCell>

                                            <TableHeaderCell>{text('lblName')}</TableHeaderCell>
                                            <TableHeaderCell>{text('lblSupplyCurrent')}</TableHeaderCell>
                                            <TableHeaderCell>{text('lblErrors')}</TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>

                                    <TableBody>
                                        {chargePoints
                                            ?.filter((chargePoint: ChargePointImport) => {
                                                if (chargePointsInGroup.length === 0) return true
                                                if (chargePointsInGroup.length > 0)
                                                    return (
                                                        chargePointsInGroup[0].ocppServerMode ===
                                                        chargePoint.ocppServerMode
                                                    )

                                                if (lbType === lbTypes.Invalid || lbType === lbTypes.Unknown)
                                                    return true
                                                return lbType === chargePoint.ocppServerMode
                                            })
                                            .map((chargePoint: ChargePointImport) => (
                                                <TableRow key={chargePoint?.id}>
                                                    <TableBodyCell>
                                                        <Input
                                                            id="error-checkbox"
                                                            type="checkbox"
                                                            data-testid={'checkbox-' + chargePoint?.id}
                                                            disabled={
                                                                (chargePoint.ocppServerMode !== lbType &&
                                                                    lbType !== 'Unknown') ||
                                                                lbType === lbTypes.Invalid ||
                                                                checkOtherErrors(chargePoint)
                                                            }
                                                            onChange={(event) =>
                                                                updateSelectedChargePoints(
                                                                    chargePoint,
                                                                    event.target.checked
                                                                )
                                                            }
                                                            checked={selectedChargePoints.includes(chargePoint)}
                                                        />
                                                    </TableBodyCell>
                                                    <TableBodyCell>
                                                        {chargePoint?.ocppServerMode !== 'None'
                                                            ? text('lblOcpp')
                                                            : text('lblCapiField')}
                                                    </TableBodyCell>

                                                    <TableBodyCell>{chargePoint?.id}</TableBodyCell>
                                                    <TableBodyCell>{chargePoint?.name} </TableBodyCell>
                                                    <TableBodyCell>{chargePoint?.supplyCurrent} </TableBodyCell>
                                                    <TableBodyCell>
                                                        {chargePoint.hasError && (
                                                            <ul className="list-unstyled">
                                                                {chargePoint.errorReason &&
                                                                    chargePoint.errorReason.map((error, index) => (
                                                                        <li key={index}>
                                                                            <i
                                                                                title={adaptErrorModalLBAddCharger(
                                                                                    error
                                                                                )}
                                                                                className="fa fa-exclamation"
                                                                                color={theme.brandColors.CA_Coral}
                                                                            ></i>
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        )}
                                                    </TableBodyCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </table>
                            </STableContainer>
                        </WidgetLayout>
                    )}
                </BootstrapModalLayout>
            )}
            {showLBSettings && (
                <LBChargePointSettings
                    chargePoints={selectedChargePoints}
                    showModal={showModal}
                    group={group}
                    updateChargePointList={updateChargePoints}
                />
            )}
        </>
    )
}
